import {useCallback, useEffect, useReducer} from 'react';
import {api} from '@/shared/services/api-client';

/**
 * Per-user, per-screen column-visibility preferences (the "Toggle Columns"
 * dropdown), persisted server-side so they follow the user across logins and
 * devices (AC2/AC6) and never bleed between users (AC3) or screens (AC4).
 *
 * A preference is a flat map { [columnId]: boolean }. Storage semantics:
 *  - No saved preference for a screen -> the screen shows its system default
 *    (new user, AC5).
 *  - A saved preference -> applied on load (AC2). Any column NOT present in the
 *    saved map (e.g. a column engineering added later) defaults to hidden so it
 *    doesn't disrupt the saved view (AC8); users opt in via the dropdown.
 *
 * Backed by a tiny module-level store (not react-query) so it works in any
 * component — including ones rendered in isolation/tests — without needing a
 * provider. One GET populates every screen's prefs; saves update the shared
 * cache optimistically.
 */
export type ColumnVisibilityMap = Record<string, boolean>;
type AllColumnPrefs = Record<string, ColumnVisibilityMap>;

let cache: AllColumnPrefs | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach((l) => l());
};

const hasAuthToken = (): boolean =>
  globalThis.window !== undefined &&
  Boolean(globalThis.window.localStorage.getItem('auth_token'));

/** Drop the cached preferences (call on logout so the next user reloads theirs). */
export const resetColumnPreferencesCache = (): void => {
  cache = null;
  inflight = null;
  notify();
};

const ensureLoaded = (): void => {
  if (cache !== null || inflight || !hasAuthToken()) return;
  inflight = api.userPreferences
    .getColumns()
    .then((res) => {
      const server = (res.data?.columns ?? {}) as AllColumnPrefs;
      // Preserve any optimistic save() writes that landed while this GET was
      // in flight — otherwise a slow response clobbers a just-applied toggle.
      cache = {...server, ...cache};
    })
    .catch(() => {
      // Preferences are non-critical: on failure fall back to defaults.
      cache = {};
    })
    .finally(() => {
      inflight = null;
      notify();
    });
};

/**
 * Merge a screen's default visibility with the user's saved preference.
 * Iterates over the *current* columns (keys of `defaults`):
 *  - no prefs  -> return defaults unchanged (system default view).
 *  - has prefs -> use the saved value when present, else hidden (new column).
 */
export function applyColumnVisibilityPrefs<T extends ColumnVisibilityMap>(
  defaults: T,
  prefs: ColumnVisibilityMap | null | undefined,
): T {
  if (!prefs) return defaults;
  const out = {} as T;
  for (const key of Object.keys(defaults) as Array<keyof T>) {
    const k = key as string;
    (out as ColumnVisibilityMap)[k] = k in prefs ? Boolean(prefs[k]) : false;
  }
  return out;
}

export function useColumnPreferences(screen: string) {
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const listener = () => forceRender();
    listeners.add(listener);
    ensureLoaded();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const prefs: ColumnVisibilityMap | null = cache?.[screen] ?? null;
  // "loaded" once the fetch settles, or immediately when logged out (defaults).
  const loaded = cache !== null || !hasAuthToken();

  // Persist the full visibility map for this screen. Optimistically updates the
  // shared cache so every mounted screen re-renders immediately; rolls back on
  // failure. Skips the network round-trip when logged out — there is no
  // account to persist against (this also keeps the local update stable
  // rather than racing a doomed request/rollback).
  const save = useCallback(
    (map: ColumnVisibilityMap) => {
      const previous = cache;
      cache = {...cache, [screen]: map};
      notify();
      if (!hasAuthToken()) return;
      api.userPreferences.saveColumns(screen, map).catch(() => {
        cache = previous;
        notify();
      });
    },
    [screen],
  );

  return {prefs, loaded, save};
}
